import React from 'react';
import { Loader2 } from 'lucide-react';

const SidebarLoadingBar = ({ 
  isLoading, 
  message = "Loading...", 
  type = "default", // "default", "categories", "content"
  className = "" 
}) => {
  if (!isLoading) return null;

  const getLoadingConfig = () => {
    switch (type) {
      case 'categories':
        return {
          message: message || "Loading categories...",
          barColor: "bg-blue-500",
          bgColor: "bg-blue-50",
          textColor: "text-blue-700",
          iconColor: "text-blue-500"
        };
      case 'content':
        return {
          message: message || "Loading content...",
          barColor: "bg-purple-500",
          bgColor: "bg-purple-50",
          textColor: "text-purple-700",
          iconColor: "text-purple-500"
        };
      case 'search':
        return {
          message: message || "Searching...",
          barColor: "bg-green-500",
          bgColor: "bg-green-50",
          textColor: "text-green-700",
          iconColor: "text-green-500"
        };
      default:
        return {
          message: message || "Loading...",
          barColor: "bg-gray-500",
          bgColor: "bg-gray-50",
          textColor: "text-gray-700",
          iconColor: "text-gray-500"
        };
    }
  };

  const config = getLoadingConfig();

  return (
    <div className={`${config.bgColor} border border-gray-200 rounded-lg p-4 m-2 ${className}`}>
      {/* Loading Header */}
      <div className="flex items-center gap-3 mb-3">
        <Loader2 className={`w-4 h-4 ${config.iconColor} animate-spin`} />
        <span className={`text-sm font-medium ${config.textColor}`}>
          {config.message}
        </span>
      </div>

      {/* Animated Progress Bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
        <div 
          className={`h-2 ${config.barColor} rounded-full animate-pulse`}
          style={{
            width: '60%',
            animation: 'loading-slide 1.5s ease-in-out infinite'
          }}
        />
      </div>

      {/* Loading Animation Styles */}
      <style jsx>{`
        @keyframes loading-slide {
          0% {
            width: 20%;
            margin-left: 0%;
          }
          50% {
            width: 60%;
            margin-left: 20%;
          }
          100% {
            width: 20%;
            margin-left: 80%;
          }
        }
      `}</style>
    </div>
  );
};

// Compact version for inline loading
export const SidebarLoadingBarCompact = ({ 
  isLoading, 
  message = "Loading...", 
  type = "default" 
}) => {
  if (!isLoading) return null;

  const config = {
    default: { barColor: "bg-gray-400", textColor: "text-gray-600" },
    categories: { barColor: "bg-blue-400", textColor: "text-blue-600" },
    content: { barColor: "bg-purple-400", textColor: "text-purple-600" },
    search: { barColor: "bg-green-400", textColor: "text-green-600" }
  }[type] || { barColor: "bg-gray-400", textColor: "text-gray-600" };

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded">
      <Loader2 className={`w-3 h-3 ${config.textColor} animate-spin`} />
      <span className={`text-xs ${config.textColor}`}>{message}</span>
      <div className="flex-1 bg-gray-200 rounded-full h-1 ml-2 overflow-hidden">
        <div 
          className={`h-1 ${config.barColor} rounded-full animate-pulse`}
          style={{
            width: '40%',
            animation: 'loading-slide-compact 1.2s ease-in-out infinite'
          }}
        />
      </div>
      <style jsx>{`
        @keyframes loading-slide-compact {
          0% { width: 20%; margin-left: 0%; }
          50% { width: 40%; margin-left: 30%; }
          100% { width: 20%; margin-left: 80%; }
        }
      `}</style>
    </div>
  );
};

// Section-specific loading bars
export const CategoryLoadingBar = ({ isLoading, message }) => (
  <SidebarLoadingBar 
    isLoading={isLoading} 
    message={message || "Loading categories..."} 
    type="categories" 
  />
);

export const ContentLoadingBar = ({ isLoading, message }) => (
  <SidebarLoadingBar 
    isLoading={isLoading} 
    message={message || "Loading content..."} 
    type="content" 
  />
);

export const SearchLoadingBar = ({ isLoading, message }) => (
  <SidebarLoadingBarCompact 
    isLoading={isLoading} 
    message={message || "Searching..."} 
    type="search" 
  />
);

export default SidebarLoadingBar;
